import React, { useEffect, useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CPInput } from "../CPInput";
import { useOrganization } from "../../context/OrganizationContext";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITY } from "../../helpers/permissions";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { isOffline } from "../../helpers/connectivity";
import {
  DEFAULT_WORK_SCHEDULE,
  ISO_WEEKDAYS,
  SCHEDULE_STATUS,
  computeExpectedCompletion,
  describeWeekdays,
  formToScheduleDoc,
  formatHoursPerDay,
  formatScheduleDate,
  parseWorkScheduleDoc,
  resolveWorkSchedule,
  scheduleToForm,
} from "../../helpers/tasks/workSchedule";
import "./adminDesktop.css";

const EXAMPLE_START = "2026-11-02"; // a Monday
const EXAMPLE_DURATIONS = [180, 480, 720];

function sameForm(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function ExampleCard({ form }) {
  const parsed = useMemo(() => formToScheduleDoc(form), [form]);
  const schedule = parsed.ok ? parseWorkScheduleDoc(parsed.doc).schedule : null;
  return (
    <div className="rb-card" data-testid="work-schedule-example">
      <div className="section-heading">EXPECTED COMPLETION EXAMPLE</div>
      <p className="admin-placeholder-note" style={{ marginBottom: "12px" }}>
        How a task&apos;s expected completion is forecast from its start date and planned duration. Due dates are
        never changed by this schedule.
      </p>
      {schedule ? (
        <ul className="adm-ws-example">
          {EXAMPLE_DURATIONS.map((minutes) => {
            const iso = computeExpectedCompletion({ startDate: EXAMPLE_START, durationMinutes: minutes }, schedule);
            return (
              <li key={minutes}>
                Start {formatScheduleDate(EXAMPLE_START)} · {minutes / 60} h →{" "}
                <strong>{iso ? formatScheduleDate(iso) : "unavailable"}</strong>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="admin-placeholder-note">Fix the schedule to see an example.</p>
      )}
    </div>
  );
}

/**
 * Organization Work Schedule (owners/admins). Saved online only and confirmed
 * by the server; the database validates it and refuses other roles.
 */
export function AdminWorkScheduleScreen({ onBack }) {
  const { settings, saveWorkSchedule, loading, error: loadError } = useOrganization();
  const { can } = usePermissions();
  const isDesktop = useIsDesktopViewport();
  const canManage = can(CAPABILITY.ADMIN_ORGANIZATION_MANAGE);
  const current = useMemo(() => resolveWorkSchedule(settings), [settings]);
  const baseForm = useMemo(() => scheduleToForm(current.schedule || DEFAULT_WORK_SCHEDULE), [current]);
  const [form, setForm] = useState(baseForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ kind: "", text: "" });
  const timezone = settings?.organization?.timezone || "";

  useEffect(() => {
    setForm(baseForm);
  }, [baseForm]);

  const dirty = !sameForm(form, baseForm);

  const toggleDay = (day) => {
    setForm((f) => {
      const has = f.workingWeekdays.includes(day);
      const next = has ? f.workingWeekdays.filter((d) => d !== day) : [...f.workingWeekdays, day].sort((a, b) => a - b);
      return { ...f, workingWeekdays: next };
    });
    setErrors((e) => ({ ...e, workingWeekdays: undefined }));
    setStatus({ kind: "", text: "" });
  };

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setStatus({ kind: "", text: "" });
  };

  const setDate = (index, patch) => {
    setField(
      "nonWorkingDates",
      form.nonWorkingDates.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    );
  };

  const handleSave = async () => {
    if (saving || !canManage) return;
    const parsed = formToScheduleDoc(form);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    if (isOffline()) {
      setStatus({ kind: "error", text: "You are offline. Connect to save the work schedule." });
      return;
    }
    setSaving(true);
    setStatus({ kind: "", text: "" });
    try {
      await saveWorkSchedule(parsed.doc);
      setStatus({ kind: "ok", text: "Work schedule saved." });
    } catch (err) {
      const message = String(err?.message || "");
      setStatus({
        kind: "error",
        text: /invalid work schedule/i.test(message)
          ? message
          : "The work schedule could not be saved. Nothing was changed.",
      });
    } finally {
      setSaving(false);
    }
  };

  const summary =
    current.status === SCHEDULE_STATUS.UNKNOWN
      ? "Not loaded yet"
      : `${describeWeekdays(current.schedule.workingWeekdays)} · ${formatHoursPerDay(current.schedule.minutesPerDay)} per day${
          current.status === SCHEDULE_STATUS.DEFAULT ? " (default)" : ""
        }`;

  return (
    <CrewPilotModuleShell
      title="Work Schedule"
      subtitle="Working days, hours per day and non-working dates"
      sectionLabel="Organization"
      onBack={onBack}
      layout={PAGE_LAYOUT.FORM}
    >
      <div className="adm-split" data-testid="work-schedule-screen">
        <div className="adm-stack">
          <div className="rb-card">
            <div className="section-heading">CURRENT SCHEDULE</div>
            <p className="admin-placeholder-note" data-testid="work-schedule-summary" style={{ marginBottom: "16px" }}>
              {summary}
              {timezone ? ` · Dates follow the organization timezone (${timezone}).` : ""}
            </p>
            {loading ? <p className="admin-placeholder-note">Loading…</p> : null}
            {loadError && current.status === SCHEDULE_STATUS.UNKNOWN ? (
              <p className="admin-placeholder-note" role="alert">
                The organization settings could not be loaded. Saving is disabled until they load.
              </p>
            ) : null}
            {!canManage ? (
              <p className="admin-placeholder-note" role="note">
                Only organization owners and admins can change the work schedule.
              </p>
            ) : null}

            <fieldset className="adm-radios adm-ws-days" style={{ border: "none", margin: 0, padding: 0, marginBottom: "16px" }}>
              <legend className="admin-field-label adm-legend" style={{ marginBottom: "10px", padding: 0 }}>
                Working days
              </legend>
              {ISO_WEEKDAYS.map((day) => (
                <label
                  key={day.value}
                  className={isDesktop ? "adm-radio" : "admin-toggle-row"}
                  style={{ cursor: canManage ? "pointer" : "default" }}
                >
                  <input
                    type="checkbox"
                    checked={form.workingWeekdays.includes(day.value)}
                    disabled={!canManage || saving}
                    onChange={() => toggleDay(day.value)}
                    aria-label={day.label}
                  />
                  <span>{day.label}</span>
                </label>
              ))}
            </fieldset>
            {errors.workingWeekdays ? (
              <p className="admin-placeholder-note" style={{ color: "#fca5a5" }} role="alert">
                {errors.workingWeekdays}
              </p>
            ) : null}

            <div className="adm-fields">
              <CPInput
                id="ws-hours"
                label="Hours per working day"
                type="number"
                min="0.25"
                max="24"
                step="0.25"
                inputMode="decimal"
                value={form.hoursPerDay}
                disabled={!canManage || saving}
                onChange={(value) => setField("hoursPerDay", value)}
              />
            </div>
            <p className="admin-placeholder-note" style={{ marginTop: "8px" }}>
              Converts a planned duration into working days (for example 8, 10, 12, 14 or 24). It is not a shift
              pattern.
            </p>
            {errors.hoursPerDay ? (
              <p className="admin-placeholder-note" style={{ color: "#fca5a5" }} role="alert">
                {errors.hoursPerDay}
              </p>
            ) : null}

            <div className="section-heading adm-subheading">NON-WORKING DATES</div>
            <p className="admin-placeholder-note" style={{ marginBottom: "12px" }}>
              Organization-wide holidays or shutdown days. They are skipped when forecasting expected completion.
            </p>
            <ul className="adm-ws-dates" data-testid="work-schedule-dates">
              {form.nonWorkingDates.map((entry, index) => (
                <li key={index} className="adm-ws-date">
                  <CPInput
                    id={`ws-date-${index}`}
                    label="Date"
                    type="date"
                    value={entry.date}
                    disabled={!canManage || saving}
                    onChange={(value) => setDate(index, { date: value })}
                  />
                  <CPInput
                    id={`ws-label-${index}`}
                    label="Label (optional)"
                    value={entry.label}
                    maxLength={80}
                    disabled={!canManage || saving}
                    onChange={(value) => setDate(index, { label: value })}
                  />
                  <button
                    type="button"
                    className="kpi-back-btn adm-ws-remove"
                    disabled={!canManage || saving}
                    onClick={() => setField("nonWorkingDates", form.nonWorkingDates.filter((_, i) => i !== index))}
                    aria-label={`Remove non-working date ${entry.date || index + 1}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            {errors.nonWorkingDates ? (
              <p className="admin-placeholder-note" style={{ color: "#fca5a5" }} role="alert">
                {errors.nonWorkingDates}
              </p>
            ) : null}
            <button
              type="button"
              className="kpi-back-btn"
              disabled={!canManage || saving}
              onClick={() => setField("nonWorkingDates", [...form.nonWorkingDates, { date: "", label: "" }])}
            >
              Add non-working date
            </button>

            <div className="adm-actions">
              <button
                type="button"
                className="kpi-back-btn adm-primary"
                onClick={handleSave}
                disabled={!canManage || saving || !dirty || current.status === SCHEDULE_STATUS.UNKNOWN}
              >
                {saving ? "Saving…" : "Save work schedule"}
              </button>
              {status.text ? (
                <p
                  className={status.kind === "ok" ? "admin-save-confirmation" : "admin-placeholder-note"}
                  style={status.kind === "error" ? { color: "#fca5a5" } : undefined}
                  role={status.kind === "error" ? "alert" : "status"}
                >
                  {status.text}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="adm-stack">
          <ExampleCard form={form} />
          <div className="rb-card" style={{ marginTop: "16px" }}>
            <div className="section-heading">NOT INCLUDED</div>
            <p className="admin-placeholder-note">
              Employee calendars, availability, shifts, crew capacity and automatic scheduling are not part of this
              setting.
            </p>
          </div>
        </div>
      </div>
    </CrewPilotModuleShell>
  );
}

export default AdminWorkScheduleScreen;
